import SecurityPage from "./pages/authentication/SecurityPage";
import { EmailLinkCallback } from "./pages/authentication/EmailLinkCallback";
import { SmsLinkCallback } from "./pages/authentication/SmsLinkCallback";
import { AuthProfilePrompt } from "./pages/authentication/components/AuthProfilePrompt";
import React, { Suspense } from "react";
import { ChakraProvider, Spinner, Center } from "@chakra-ui/react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import { apolloClient } from "./apollo/client";
import Home from "./pages/Home";
import LoginPage from "./pages/authentication/LoginPage";
import { brandConfig } from "./brandConfig";
import theme from "./theme";

// Module configurations (core tenant modules only)
import clientsModuleConfig from "./pages/clients/moduleConfig";
import { calendarsModuleConfig } from "./pages/calendars/moduleConfig";
import opportunitiesModuleConfig from "./pages/opportunities/moduleConfig";
import emailsModuleConfig from "./pages/emails/moduleConfig";
import companyKnowledgeModuleConfig from "./pages/companyknowledge/moduleConfig";
import lmModuleConfig from "./pages/lm/moduleConfig";
import mediaCDNModuleConfig from "./pages/media-cdn/moduleConfig";
import pModuleConfig from "./pages/p/moduleConfig";
import rewardsModuleConfig from "./pages/rewards/moduleConfig";
import billsModuleConfig from "./pages/bills/moduleConfig";
import profileModuleConfig from "./pages/profile/moduleConfig";
import { TomProvider } from "./pages/tom/TomProvider";
import tomModuleConfig from "./pages/tom/moduleConfig";
import meetModuleConfig from "./pages/meet/moduleConfig";
const TomPanel = React.lazy(() => import("./pages/tom/TomPanel"));

// Combine all module configurations
const modules = [
  profileModuleConfig, // User profile management — view/edit/settings/shipping/payments/subscriptions
  clientsModuleConfig,
  calendarsModuleConfig,
  opportunitiesModuleConfig,
  pModuleConfig, // Proposals v2 - Enhanced proposal management with opportunity linking
  billsModuleConfig,
  emailsModuleConfig,
  companyKnowledgeModuleConfig,
  lmModuleConfig, // Learning Management System with course creation and enrollment
  mediaCDNModuleConfig, // Media CDN - File hosting with IPFS storage
  rewardsModuleConfig, // BB Token rewards - issue, track, and redeem tokens
  tomModuleConfig, // Tom AI floating assistant panel
  meetModuleConfig, // Meet — self-hosted LiveKit video meetings. /meet/:room is public (guests join, no login); host surface + recordings/transcripts are MEET_USER/MEET_ADMIN, resolved from the backend
];

// Protected Route Component
import { ProtectedRoute } from "./components/ProtectedRoute";

// Loading component for lazy-loaded routes
const LoadingFallback = () => (
  <Center h="100vh">
    <Spinner size="xl" color={brandConfig.colors.primary} />
  </Center>
);

// Bill-related imports (special pages not in the module config)
import BillPaymentSuccess from "./pages/bills/BillPaymentSuccess";
const SubscribePage = React.lazy(() => import("./pages/bills/SubscribePage"));

// Public Booking imports (NO AUTH REQUIRED)
import PublicBookingPage from "./pages/calendars/booking/PublicBookingPage";
import PublicBookingEventPage from "./pages/calendars/booking/PublicBookingEventPage";
import PublicBookingConfirmPage from "./pages/calendars/booking/PublicBookingConfirmPage";
import PublicBookingSuccessPage from "./pages/calendars/booking/PublicBookingSuccessPage";
import ManageBooking from "./pages/calendars/ManageBooking";

// Contexts and Layout
import { AuthProvider } from "./contexts/AuthContext";
import { ModulePermissionsProvider, useModulePermissions } from "./contexts/ModulePermissionsContext";
import { IpfsGatewayLoader } from "./components/IpfsGatewayLoader";

// Profile routes are now handled by profileModuleConfig (dynamic routing with useLayout)

// Floating navbar component
import { FloatingNavbar } from "./components/FloatingNavbar";
import { ModuleLayout } from "./components/ModuleLayout";
import { useAuth } from "./contexts/AuthContext";


const AppContent = () => {
  const { isAuthenticated } = useAuth();
  const { permissions: backendPerms } = useModulePermissions();

  // Resolve permissionLevel → actual permission strings from backend
  // Modules using the new pattern have permissionLevel on routes/nav and empty permission arrays.
  // Modules not yet migrated still have hardcoded permission arrays — those pass through unchanged.
  const resolvedModules = React.useMemo(() => {
    return modules.map(module => {
      const modulePerms = backendPerms[module.id];
      if (!modulePerms) return module; // Not migrated yet — use hardcoded permissions

      return {
        ...module,
        routes: module.routes?.map((route: any) => {
          const level = (route as any).permissionLevel;
          if (!level) return route; // No permissionLevel — use existing permissions array
          return { ...route, permissions: (modulePerms as any)[level] || [] };
        }),
        navigation: module.navigation?.map((navItem: any) => {
          const level = (navItem as any).permissionLevel;
          if (!level) return navItem; // No permissionLevel — use existing permissions array
          return { ...navItem, permissions: (modulePerms as any)[level] || [] };
        }),
        permissions: {
          ...module.permissions,
          view: modulePerms.view || module.permissions.view,
          create: modulePerms.create || module.permissions.create,
          edit: modulePerms.edit || module.permissions.edit,
          delete: modulePerms.delete || module.permissions.delete,
          ...(modulePerms.financial ? { financial: modulePerms.financial } : {}),
        },
      };
    });
  }, [backendPerms]);

  return (
    <>
      {/* Show floating navbar only when authenticated */}
      {isAuthenticated && <FloatingNavbar modules={resolvedModules} />}

      {/* Tom AI floating assistant panel */}
      {isAuthenticated && (
        <React.Suspense fallback={null}>
          <TomPanel />
        </React.Suspense>
      )}

      {/* Each page now includes its own NavbarWithCallToAction and FooterWithFourColumns for consistency */}
      <Routes>
        <Route path="/security" element={<ProtectedRoute requiredPermissions={[]}><SecurityPage /></ProtectedRoute>} />
        <Route path="/auth/email-link" element={<EmailLinkCallback />} />
        <Route path="/auth/sms-link" element={<SmsLinkCallback />} />
        {/* Home page */}
        <Route path="/" element={<Home />} />

        {/* Login page */}
        <Route path="/login" element={<LoginPage />} />

        {/* PUBLIC BOOKING PAGES (NO AUTH REQUIRED) */}
        {/* These routes allow public visitors to book appointments without logging in */}
        <Route path="/book/:slug" element={<PublicBookingPage />} />
        <Route path="/book/:slug/:eventTypeId" element={<PublicBookingEventPage />} />
        <Route path="/book/:slug/confirm" element={<PublicBookingConfirmPage />} />
        <Route path="/book/:slug/success" element={<PublicBookingSuccessPage />} />
        <Route path="/calendars/booking/manage/:token" element={<ManageBooking />} />

        {/* Special bill routes not in the module */}
        <Route path="/bill/:id/payment-success" element={<BillPaymentSuccess />} />
        {/* Public subscription product landing page */}
        <Route path="/subscribe/:slug" element={<Suspense fallback={<LoadingFallback />}><SubscribePage /></Suspense>} />

        {/* Dynamic Module Routes — permissions resolved from backend for migrated modules */}
        {resolvedModules.map((module) =>
          module.routes?.map((route: any) => {
            const PageComponent = route.component;
            const content = route.useLayout ? (
              <ModuleLayout><PageComponent /></ModuleLayout>
            ) : (
              <PageComponent />
            );

            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  route.public ? (
                    <Suspense fallback={<LoadingFallback />}>
                      {content}
                    </Suspense>
                  ) : (
                    <ProtectedRoute requiredPermissions={route.permissions || []}>
                      <Suspense fallback={<LoadingFallback />}>
                        {content}
                      </Suspense>
                    </ProtectedRoute>
                  )
                }
              />
            );
          })
        )}

        {/* Profile routes are now in profileModuleConfig — see modules array above. They flow through the dynamic route loop with ModuleLayout. */}

      </Routes>
    </>
  );
};

export const App = () => {
  // Check and set tenantId if missing
  React.useEffect(() => {
    const storedTenantId = localStorage.getItem('tenantId');
    if (!storedTenantId) {
      // Use the tenantId from brandConfig as default
      if (brandConfig.tenantId) {
        localStorage.setItem('tenantId', brandConfig.tenantId);
      }
    }
  }, []);

  return (
    <Router>
      <ApolloProvider client={apolloClient}>
        <IpfsGatewayLoader>
          <AuthProvider>
            <ModulePermissionsProvider>
              <ChakraProvider theme={theme}>
                <TomProvider>
                  <AuthProfilePrompt />
                    <AppContent />
                </TomProvider>
              </ChakraProvider>
            </ModulePermissionsProvider>
          </AuthProvider>
        </IpfsGatewayLoader>
      </ApolloProvider>
    </Router>
  );
};
